import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import SliderItem from './bottom-card.js';

const EASE = [0.16, 1, 0.3, 1];

/* The destinations come from screens/registry.js, one list per side.
 *
 * They used to be written out twice, with a comment explaining that the
 * duplicate existed to give large screens something to scroll. The rail
 * centres itself now and scrolls only when it genuinely overflows, so the
 * second copy is gone: it put seven duplicate controls in the
 * accessibility tree, and clicking one moved the highlight to its twin. */
function BottomSlider({ items, home = '', onItemClick, selectedScreen }) {
    const track = useRef(null);

    /* Tapping the destination you are already on returns to this side's
       home. That toggle is the existing behaviour and the only way back
       on the resident side, since there is no home button. */
    const handleClick = (item) => {
        onItemClick(selectedScreen === item ? home : item);
    };

    /* With seventeen destinations the active one can be off the end of
       the row, most often when a tile on the welcome screen chose it, so
       the row brings it into view. Only the row scrolls: scrollIntoView
       would also nudge every clipped ancestor, and on a phone that slid
       the whole kiosk a few pixels left of the screen. */
    useEffect(() => {
        const row = track.current;
        const active = row?.querySelector('.rail__item--active');
        if (!row || !active || !row.scrollTo) return;

        const target = active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2;
        row.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }, [selectedScreen, items]);

    return (
        <motion.nav
            className="rail"
            aria-label="Building services"
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        >
            <div className="rail__track" ref={track}>
                {items.map((item, i) => (
                    <motion.div
                        key={item.screen}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: Math.min(0.2 + i * 0.04, 0.7), ease: EASE }}
                    >
                        <SliderItem
                            Icon={item.Icon}
                            label={item.label}
                            isActive={selectedScreen === item.screen}
                            onClick={() => handleClick(item.screen)}
                        />
                    </motion.div>
                ))}
            </div>
        </motion.nav>
    );
}

export default BottomSlider;
